import { useCallback, useEffect, useMemo, useState } from "react";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Alert } from "@/components/ui/Alert";
import { Spinner } from "@/components/ui/Spinner";
import { Table } from "@/components/ui/Table";
import { Dialog } from "@/components/ui/Dialog";
import { TextInput, Checkbox } from "@/components/ui/Field";
import { formatDateTime } from "@/lib/format";
import {
  listUserSessions,
  revokeAllSessions,
  revokeSession,
  revokeUserSessions,
  type UserSession,
} from "../../lib/settingsApi";

type ConfirmScope = "single" | "user" | "all";

export function UserSessionsSection() {
  const [sessions, setSessions] = useState<UserSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState("");
  const [includeRevoked, setIncludeRevoked] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmScope, setConfirmScope] = useState<ConfirmScope>("single");
  const [confirmSid, setConfirmSid] = useState("");
  const [confirmUser, setConfirmUser] = useState("");
  const [confirmTitle, setConfirmTitle] = useState("");
  const [confirmText, setConfirmText] = useState("");
  const [reason, setReason] = useState("");
  const [keepSelf, setKeepSelf] = useState(true);
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      setSessions(
        await listUserSessions({
          include_revoked: includeRevoked,
          include_expired: false,
          limit: 500,
        }),
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : "Sitzungen konnten nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  }, [includeRevoked]);

  useEffect(() => {
    reload();
  }, [reload]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return sessions;
    return sessions.filter(
      (s) =>
        s.user_login?.toLowerCase().includes(q) ||
        s.user_display_name?.toLowerCase().includes(q),
    );
  }, [sessions, filter]);

  function openConfirm(opts: {
    title: string;
    text: string;
    scope: ConfirmScope;
    sid?: string;
    userLogin?: string;
  }) {
    setConfirmTitle(opts.title);
    setConfirmText(opts.text);
    setConfirmScope(opts.scope);
    setConfirmSid(opts.sid || "");
    setConfirmUser(opts.userLogin || "");
    setReason("");
    setKeepSelf(true);
    setConfirmError(null);
    setConfirmOpen(true);
  }

  async function handleConfirm() {
    setConfirming(true);
    setConfirmError(null);
    try {
      if (confirmScope === "single") await revokeSession(confirmSid, reason);
      else if (confirmScope === "user") await revokeUserSessions(confirmUser, reason, keepSelf);
      else await revokeAllSessions(reason, keepSelf);
      setConfirmOpen(false);
      await reload();
    } catch (err) {
      setConfirmError(err instanceof Error ? err.message : "Abmeldung fehlgeschlagen.");
    } finally {
      setConfirming(false);
    }
  }

  return (
    <Card
      title="Aktive Anmeldungen"
      description="Sitzungen einsehen und beenden."
      actions={
        <Button
          type="button"
          variant="danger"
          onClick={() =>
            openConfirm({
              title: "Alle Benutzer abmelden",
              text: "Alle aktiven Sitzungen aller Benutzer beenden?",
              scope: "all",
            })
          }
        >
          Alle abmelden
        </Button>
      }
    >
      <div className="mb-4 flex flex-wrap gap-3">
        <TextInput
          placeholder="Benutzer suchen …"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
        <Checkbox
          label="Widerrufene anzeigen"
          checked={includeRevoked}
          onChange={(e) => setIncludeRevoked(e.target.checked)}
        />
        <Button type="button" variant="secondary" onClick={reload}>
          Aktualisieren
        </Button>
      </div>
      {loading && <Spinner />}
      {error && <Alert variant="error" title="Fehler">{error}</Alert>}
      {!loading && (
        <Table<UserSession>
          rowKey={(r) => r.sid}
          rows={filtered}
          stickyEndColumnCount={1}
          columns={[
            {
              key: "user",
              header: "Benutzer",
              sortable: true,
              sortValue: (r) => r.user_display_name || r.user_login,
              render: (r) => r.user_display_name || r.user_login,
            },
            {
              key: "login",
              header: "Login",
              sortable: true,
              sortValue: (r) => r.user_login,
              render: (r) => r.user_login,
            },
            {
              key: "last",
              header: "Zuletzt aktiv",
              sortable: true,
              sortValue: (r) => r.last_seen_at,
              render: (r) => (r.last_seen_at ? formatDateTime(r.last_seen_at) : "—"),
            },
            {
              key: "status",
              header: "Status",
              sortable: true,
              sortValue: (r) => !r.revoked_at,
              render: (r) => (r.revoked_at ? "Widerrufen" : "Aktiv"),
            },
            {
              key: "actions",
              header: "",
              render: (r) => (
                <div className="flex justify-end gap-1">
                  {!r.revoked_at && (
                    <Button
                      size="sm"
                      variant="danger"
                      onClick={() =>
                        openConfirm({
                          title: "Sitzung beenden",
                          text: `Sitzung von „${r.user_display_name || r.user_login}" beenden?`,
                          scope: "single",
                          sid: r.sid,
                        })
                      }
                    >
                      Beenden
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() =>
                      openConfirm({
                        title: "Benutzer abmelden",
                        text: `Alle Sitzungen von „${r.user_login}" beenden?`,
                        scope: "user",
                        userLogin: r.user_login,
                      })
                    }
                  >
                    User
                  </Button>
                </div>
              ),
            },
          ]}
        />
      )}
      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title={confirmTitle}
        description={confirmText}
        footer={
          <>
            <Button variant="secondary" onClick={() => setConfirmOpen(false)}>Abbrechen</Button>
            <Button variant="danger" onClick={handleConfirm} loading={confirming}>
              Bestätigen
            </Button>
          </>
        }
      >
        {confirmError && <Alert variant="error" title="Fehler">{confirmError}</Alert>}
        <TextInput label="Grund (optional)" value={reason} onChange={(e) => setReason(e.target.value)} />
        {confirmScope !== "single" && (
          <Checkbox
            className="mt-3"
            label="Eigene Sitzung erhalten"
            checked={keepSelf}
            onChange={(e) => setKeepSelf(e.target.checked)}
          />
        )}
      </Dialog>
    </Card>
  );
}
